import { URL } from 'react-native-url-polyfill';
import { Platform } from 'react-native';
import { getStorage } from '@/store/storage';

const KEY = 'referralCode';

export function normalizeReferralCode(value) {
  if (typeof value !== 'string') return '';
  const code = value.trim().toLowerCase();
  return /^(?:[a-f0-9]{8}|[a-f0-9]{12})$/.test(code) ? code : '';
}

export function referralCodeFromUrl(value) {
  if (typeof value !== 'string' || value.length > 2048) return '';
  try {
    const UrlParser = Platform.OS === 'web' ? globalThis.URL : URL;
    const url = new UrlParser(value, 'https://gab.ai');
    const native = url.protocol === 'gabai:';
    if (!native && !['https:', 'http:'].includes(url.protocol)) return '';
    if (!native && !['gab.ai', 'www.gab.ai', 'localhost', '127.0.0.1'].includes(url.hostname)) return '';
    const path = native ? `${url.hostname ? `/${url.hostname}` : ''}${url.pathname || '/'}` : url.pathname;
    if (!['/', '/signup', '/signup/', '/app', '/app/'].includes(path)) return '';
    if (url.searchParams.getAll('ref').length !== 1) return '';
    return normalizeReferralCode(url.searchParams.get('ref'));
  } catch { return ''; }
}

export function referralAppUrl(code) {
  const normalized = normalizeReferralCode(code);
  return normalized ? `gabai://signup?ref=${normalized}` : null;
}

export function createPendingReferral(storage) {
  let code = '', loaded = false, hydration;
  let writes = Promise.resolve();
  const listeners = new Set();
  const notify = () => listeners.forEach(listener => listener());
  const persist = code => {
    writes = writes.then(() => code ? storage.setItem(KEY, code) : storage.removeItem(KEY)).catch(() => {});
    return writes;
  };
  const hydrate = () => {
    if (loaded) return Promise.resolve(code);
    if (!hydration) hydration = Promise.resolve().then(() => storage.getItem(KEY)).catch(() => null).then(value => {
      // An incoming link wins over an older storage read.
      if (!loaded) { code = normalizeReferralCode(value); loaded = true; notify(); }
      return code;
    });
    return hydration;
  };
  return {
    hydrate,
    snapshot: () => code,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    capture(value) {
      const normalized = normalizeReferralCode(value);
      if (!normalized) return Promise.resolve();
      code = normalized;
      loaded = true;
      notify();
      return persist(code);
    },
    async read() {
      await hydrate();
      return code || null;
    },
    async clear(expectedCode) {
      await hydrate();
      if (!expectedCode || code !== expectedCode) return;
      code = ''; loaded = true; notify();
      await persist('');
    },
  };
}

export const pendingReferral = createPendingReferral({
  getItem: key => getStorage().getItem(key),
  setItem: (key, value) => getStorage().setItem(key, value),
  removeItem: key => getStorage().removeItem(key),
});
